// ABOUTME: The dealership a screen works in, as the assignment line: icon, "Dealership: name" and a "Change" button.
// ABOUTME: Shared by the catalogue's header and the cart's; resolves the dealership's name, the button opens the picker.

import { Box } from '@ds/coolshop';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { AssignmentLine } from '../../node-assignment/components/AssignmentLine';
import { useOptionalDealershipChoice } from '../context/dealershipChoiceHooks';
import type { SelectableDealerships } from '../types';
import { needsDealershipChoice } from '../utils/dealershipChoice';

type DealershipLabelProps = {
    /** the dealership shown, undefined for "None (company)" */
    dealershipId: string | undefined;
    /** opens the dealership picker */
    onChange: () => void;
    /** takes its container's width and puts the button at its far end, as in the cart's header */
    buttonAtEnd?: boolean;
    /** renders nothing when the user has nothing to choose (one dealership or none) */
    hideWithoutChoice?: boolean;
    /** a row of a list, spaced and bordered below as the cart's lines (and gone with the line) */
    listRow?: boolean;
    /**
     * centres the icon on a column of this width from the line's start, lining it up with the images
     * of a list below (the cart's lines); the text keeps following the icon
     */
    iconColumnWidth?: string;
};

export function DealershipLabel({
    dealershipId,
    onChange,
    buttonAtEnd = false,
    hideWithoutChoice = false,
    listRow = false,
    iconColumnWidth,
}: DealershipLabelProps) {
    const { formatMessage } = useIntl();
    const loadSelectable = useOptionalDealershipChoice()?.loadSelectable;
    const [selectable, setSelectable] = useState<SelectableDealerships | null>(null);

    useEffect(() => {
        if (!loadSelectable) return;
        loadSelectable()
            .then(setSelectable)
            .catch(() => setSelectable(null));
    }, [loadSelectable]);

    if (hideWithoutChoice && (!selectable || !needsDealershipChoice(selectable))) return null;

    // The dealership's name, the id alone until the names are loaded.
    const dealership = selectable?.dealerships.find(({ id }) => id === dealershipId);
    let dealershipLabel = formatMessage({
        id: 'dealership.picker.none',
        defaultMessage: 'None (company)',
    });
    if (dealership) dealershipLabel = dealership.name;
    else if (dealershipId) dealershipLabel = dealershipId;

    const line = (
        <AssignmentLine
            target="dealership"
            value={dealershipLabel}
            action={{
                label: formatMessage({ id: 'dealership.label.change', defaultMessage: 'Change' }),
                onClick: onChange,
            }}
            actionAtEnd={buttonAtEnd}
            iconColumnWidth={iconColumnWidth}
        />
    );
    if (!listRow) return line;
    return (
        <Box py={1.5} borderBottom="1px solid" borderColor="divider">
            {line}
        </Box>
    );
}

export default DealershipLabel;
